// AI-CONTEXT: See docs/features/api-data-fetching.md
import { useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../firebase/config'
import defaultDates from '../../utils/defaultDates'
import useHierarchyData from '../../hooks/useHierarchyData'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import toJsDate, { toDateKey } from '../../helpers/dataHelpers/toJsDate'
import { definitionCollection } from '../../firebase/publicDefinitions';

// ---- lightweight in-memory caches to avoid duplicate lookups
const outputTitleCache = new Map()   // OutputID -> Title
const projectNameCache = new Map()   // CompagnyID -> CommunityName

const getOutputTitle = async (outputID) => {
  if (!outputID) return 'Unknown Output'
  if (outputTitleCache.has(outputID)) return outputTitleCache.get(outputID)

  const snap = await definitionCollection('Outputs').where('ID', '==', outputID).get()
  const title = snap.empty ? 'Unknown Output' : (snap.docs[0].data().Title || 'Unknown Output')
  outputTitleCache.set(outputID, title)
  return title
}

const getProjectName = async (projectID) => {
  if (!projectID) return 'Unknown Project'
  if (projectNameCache.has(projectID)) return projectNameCache.get(projectID)

  const snap = await db.collection('CompagnyMeta').where('CompagnyID', '==', projectID).get()
  const name = snap.empty ? 'Unknown Project' : (snap.docs[0].data().CommunityName || 'Unknown Project')
  projectNameCache.set(projectID, name)
  return name
}

const transformData = async (rawArray, outputsList) => {
  const grouped = {}
  const projectValues = {}
  let totalValue = 0
  const titleMap = Object.fromEntries((outputsList || []).map(o => [o.ID, o.Title]))

  for (const doc of rawArray) {
    const dateString = toDateKey(doc?.Timestamp)
    if (!dateString) continue

    const title = titleMap[doc.OutputID] || (await getOutputTitle(doc.OutputID))
    const projName = doc._projectName || (await getProjectName(doc.CompagnyID || doc.CompanyID))
    const value = Number(doc?.Value ?? doc?.Result ?? 0)

    if (!grouped[dateString]) grouped[dateString] = { name: dateString }
    if (!grouped[dateString][title]) grouped[dateString][title] = { total: 0, projects: {} }

    grouped[dateString][title].total += value
    grouped[dateString][title].projects[projName] =
      (grouped[dateString][title].projects[projName] || 0) + value

    projectValues[projName] = (projectValues[projName] || 0) + value
    totalValue += value
  }

  const array = Object.values(grouped)
  array.sort((a, b) => {
    const [dA, mA, yA] = a.name.split('-').map(Number)
    const [dB, mB, yB] = b.name.split('-').map(Number)
    return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB)
  })

  return { array, projectValues, totalValue }
}

const addResults = (arr, graphType) => {
  if (graphType !== 'line' || arr.length <= 1) return

  for (let i = 1; i < arr.length; i++) {
    const curr = arr[i]
    const prev = arr[i - 1]
    Object.keys(prev).forEach((key) => {
      if (key === 'name') return
      if (!curr[key]) {
        curr[key] = JSON.parse(JSON.stringify(prev[key]))
      } else {
        curr[key].total =
          curr[key].total === 0 ? prev[key].total : curr[key].total + prev[key].total

        Object.keys(prev[key].projects).forEach((proj) => {
          curr[key].projects[proj] =
            !curr[key].projects[proj] || curr[key].projects[proj] === 0
              ? prev[key].projects[proj]
              : curr[key].projects[proj] + prev[key].projects[proj]
        })
      }
    })
  }
}

const computeGrowth = (arr) => {
  if (!arr.length) return { perOutput: {}, perProject: {}, average: 0 }

  const first = arr[0]
  const last = arr[arr.length - 1]
  const perOutput = {}
  const perProject = {}
  const growths = []

  Object.keys(last).forEach((key) => {
    if (key === 'name') return
    const startVal = first[key]?.total || 0
    const endVal = last[key].total
    const pct = startVal !== 0 ? ((endVal - startVal) / startVal) * 100 : endVal * 100
    perOutput[key] = pct
    growths.push(pct)

    perProject[key] = {}
    Object.keys(last[key].projects).forEach((proj) => {
      const startP = first[key]?.projects[proj] || 0
      const endP = last[key].projects[proj]
      perProject[key][proj] = startP !== 0 ? ((endP - startP) / startP) * 100 : endP * 100
    })
  })

  return {
    perOutput,
    perProject,
    average: growths.reduce((s, g) => s + g, 0) / (growths.length || 1),
  }
}

/**
 * Custom hook: fetches & shapes OutputEvent data for one outputId.
 */
export const useOutputData = ({
  outputId = '',
  startDate = null,
  endDate = null,
  graphType = 'line',
  outputsList = null,
  personaId = null,
}) => {
  const [data, setData] = useState([])
  const [growth, setGrowth] = useState({ perOutput: {}, perProject: {}, average: 0 })
  const [breakdown, setBreakdown] = useState({ projectValues: {}, totalValue: 0 })
  const [error, setError] = useState(null)

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), [])
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw])
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw])
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs])
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs])

  const mountedRef = useRef(true)

  // Fetch records via Cloud Function
  const {
    records,
    hierarchy: projectHierarchy,
    loading: isLoading,
    error: fetchError,
  } = useHierarchyData({
    syncType: 'OutputEvent',
    id: outputId,
    startDate: startMs,
    endDate: endMs,
    personaId: personaId || undefined,
  })

  // Process records when they arrive
  useEffect(() => {
    mountedRef.current = true

    if (isLoading || !records.length) {
      if (!isLoading) {
        setData([])
        setGrowth({ perOutput: {}, perProject: {}, average: 0 })
        setBreakdown({ projectValues: {}, totalValue: 0 })
      }
      return
    }

    let cancelled = false

    const process = async () => {
      try {
        // Sort by time ascending
        const sorted = [...records].sort((a, b) => {
          const aMs = typeof a.Timestamp?.toMillis === 'function'
            ? a.Timestamp.toMillis()
            : +toJsDate(a.Timestamp)
          const bMs = typeof b.Timestamp?.toMillis === 'function'
            ? b.Timestamp.toMillis()
            : +toJsDate(b.Timestamp)
          return aMs - bMs
        })

        const { array: shaped, projectValues, totalValue } = await transformData(sorted, outputsList)
        if (shaped.length) {
          addResults(shaped, graphType)
        }
        const g = computeGrowth(shaped)

        if (!cancelled && mountedRef.current) {
          setData(shaped)
          setGrowth(g)
          setBreakdown({ projectValues, totalValue })
          setError(null)
        }
      } catch (err) {
        console.error('useOutputData processing error:', err)
        if (!cancelled && mountedRef.current) {
          setError(err)
          setData([])
          setGrowth({ perOutput: {}, perProject: {}, average: 0 })
          setBreakdown({ projectValues: {}, totalValue: 0 })
        }
      }
    }

    process()
    return () => {
      cancelled = true
      mountedRef.current = false
    }
  }, [records, isLoading, graphType, outputsList])

  return {
    data,
    isLoading,
    growth,
    projectHierarchy: projectHierarchy || {},
    projectValues: breakdown.projectValues,
    totalValue: breakdown.totalValue,
    totalResults: records.length || 0,
    records,
    // The resolved period behind `records`, so a caller fetching related data
    // (the field answers in the tooltip) asks for exactly the same window.
    startMs,
    endMs,
    error: error || fetchError,
  }
}

export default useOutputData
